import React, { useEffect, useState } from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { WallPost, WallStance } from '../../core/types/payloads/wall.types';
import { useRoomParticipants } from '../../core/client/hooks';
import { useRoomContext } from '../../core/client/RoomContext';

function Shell({
  badge,
  title,
  prompt,
  children,
  host = false,
}: {
  badge?: string;
  title: string;
  prompt: string;
  children: React.ReactNode;
  host?: boolean;
}) {
  return (
    <div className="plugin-shell rounded-2xl p-5 sm:p-8">
      {badge && (
        <span className="plugin-shell__badge mb-3 inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-widest">
          {badge}
        </span>
      )}
      <h3 className={`${host ? 'plugin-host-title plugin-host-title--lg' : 'font-display text-xl font-black sm:text-2xl'}`}>
        {title}
      </h3>
      <p className={`mt-2 ${host ? 'plugin-host-prompt plugin-host-prompt--lg' : 'text-base font-bold text-slate-700 sm:text-lg'}`}>
        {prompt}
      </p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function PostIt({
  post,
  stance,
  author,
  highlighted,
  size = 'md',
  onHighlight,
}: {
  post: WallPost;
  stance: WallStance;
  author?: string;
  highlighted?: boolean;
  size?: 'md' | 'lg';
  onHighlight?: () => void;
}) {
  return (
    <article
      className={`wall-post-it ${highlighted ? 'wall-post-it--spotlight' : ''}`}
      style={{
        ['--stance-color' as string]: stance.color,
        borderLeftColor: stance.color,
      }}
      onClick={onHighlight}
      role={onHighlight ? 'button' : undefined}
    >
      <p className={`font-bold leading-snug ${size === 'lg' ? 'text-base sm:text-lg' : 'text-sm'}`}>{post.text}</p>
      {author && <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500">{author}</p>}
    </article>
  );
}

function WallColumns({
  stances,
  posts,
  participants,
  highlightedPostId,
  size = 'md',
  onHighlight,
  maskNames,
}: {
  stances: WallStance[];
  posts: Record<string, WallPost>;
  participants: { participantId: string; displayName: string }[];
  highlightedPostId?: string;
  size?: 'md' | 'lg';
  onHighlight?: (participantId: string) => void;
  maskNames?: boolean;
}) {
  const nameMap = new Map(participants.map((p) => [p.participantId, p.displayName]));

  return (
    <div className={`wall-columns grid gap-3 ${stances.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      {stances.map((stance) => {
        const columnPosts = Object.values(posts).filter((p) => p.stanceId === stance.id);
        return (
          <section key={stance.id} className="wall-column rounded-xl p-3" style={{ borderTop: `4px solid ${stance.color}` }}>
            <header className="mb-3 flex items-center justify-between gap-2">
              <h4 className="font-display text-xs font-black uppercase tracking-widest" style={{ color: stance.color }}>
                {stance.label}
              </h4>
              <span className="text-[10px] font-black text-slate-500">{columnPosts.length}</span>
            </header>
            <div className="space-y-2">
              {columnPosts.length === 0 && (
                <p className="py-6 text-center text-xs font-bold text-slate-400">Sin argumentos aún</p>
              )}
              {columnPosts.map((post) => (
                <PostIt
                  key={post.participantId}
                  post={post}
                  stance={stance}
                  author={maskNames ? undefined : nameMap.get(post.participantId)}
                  highlighted={highlightedPostId === post.participantId}
                  size={size}
                  onHighlight={onHighlight ? () => onHighlight(post.participantId) : undefined}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function WallHostView(props: ActivityViewProps<'wall'>) {
  const { payload, runtime, dispatch } = props;
  const { participants } = useRoomParticipants();

  return (
    <Shell badge="Muro de argumentos" title={payload.title} prompt={payload.prompt} host>
      <WallColumns
        stances={payload.stances}
        posts={runtime.posts}
        participants={participants}
        highlightedPostId={runtime.highlightedPostId}
        onHighlight={(id) => dispatch({ type: 'HIGHLIGHT_POST', payload: { participantId: id } })}
      />
      {runtime.highlightedPostId && (
        <button
          type="button"
          className="mt-4 text-xs font-black uppercase tracking-wide text-slate-600 underline"
          onClick={() => dispatch({ type: 'CLEAR_HIGHLIGHT', payload: {} })}
        >
          Quitar destacado
        </button>
      )}
    </Shell>
  );
}

export function WallStudentView(props: ActivityViewProps<'wall'>) {
  const { payload, runtime, participantId, phase } = props;
  const { submitStudentInput, state } = useRoomContext();
  const [text, setText] = useState('');
  const [stanceId, setStanceId] = useState(payload.stances[0]?.id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';
  const myPost = participantId ? runtime.posts[participantId] : undefined;
  const max = payload.maxChars ?? 140;
  const lastError = state.lastError;

  useEffect(() => {
    const first = payload.stances[0];
    if (first && !payload.stances.some((s) => s.id === stanceId)) {
      setStanceId(first.id);
    }
  }, [payload.stances, stanceId]);

  const publish = async () => {
    if (!text.trim() || phase !== 'ACTIVE' || !stanceId || submitting) return;
    setSubmitting(true);
    try {
      await submitStudentInput('POST', { text: text.trim(), stanceId });
    } finally {
      setSubmitting(false);
    }
  };

  if (waiting) {
    return (
      <Shell badge="El Muro" title={payload.title} prompt={payload.prompt}>
        <p className="plugin-wait-banner">
          Espera a que el profesor pulse <strong className="text-[var(--sala-electric-purple)]">Comenzar</strong>
        </p>
      </Shell>
    );
  }

  if (myPost) {
    const stance = payload.stances.find((s) => s.id === myPost.stanceId);
    return (
      <Shell badge="Argumento enviado" title={payload.title} prompt={payload.prompt}>
        <PostIt post={myPost} stance={stance ?? payload.stances[0]} />
        <p className="plugin-muted-note mt-4">Tu post-it está en el muro del proyector</p>
      </Shell>
    );
  }

  return (
    <Shell badge="Escribe tu argumento" title={payload.title} prompt={payload.prompt}>
      <div className="mb-4 flex flex-wrap gap-2">
        {payload.stances.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`wall-stance-chip${stanceId === s.id ? ' wall-stance-chip--active' : ''}`}
            style={
              stanceId === s.id
                ? { borderColor: s.color, background: `${s.color}18`, color: s.color }
                : undefined
            }
            onClick={() => setStanceId(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <textarea
        className="wall-textarea"
        maxLength={max}
        placeholder="Tu argumento (máx. 140 caracteres)"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <p className="mt-1 text-right text-xs font-bold text-slate-500">
        {text.length}/{max}
      </p>
      {lastError && (
        <p className="mt-3 rounded-xl border-[1.5px] border-red-800 bg-red-100 px-3 py-2 text-center text-sm font-bold text-red-800 shadow-[3px_3px_0_0_#991b1b]">
          No se pudo publicar: {lastError}
        </p>
      )}
      <button
        type="button"
        disabled={!text.trim() || !stanceId || phase !== 'ACTIVE' || submitting}
        className="sala-btn-hero mt-4 w-full text-xs disabled:opacity-40"
        onClick={() => void publish()}
      >
        {submitting ? 'Publicando…' : 'Publicar en el muro'}
      </button>
    </Shell>
  );
}

export function WallProjectorView(props: ActivityViewProps<'wall'>) {
  const { payload, runtime } = props;
  const { participants } = useRoomParticipants();
  const highlighted = runtime.highlightedPostId ? runtime.posts[runtime.highlightedPostId] : undefined;
  const highlightStance = highlighted ? payload.stances.find((s) => s.id === highlighted.stanceId) : undefined;

  return (
    <Shell badge="Debate en vivo" title={payload.title} prompt={payload.prompt} host>
      {highlighted && highlightStance && (
        <div
          className="mb-6 rounded-2xl border-[1.5px] p-6 text-center shadow-[4px_4px_0_0_#020617]"
          style={{ borderColor: highlightStance.color, background: `${highlightStance.color}12` }}
        >
          <p className="text-xs font-black uppercase tracking-widest" style={{ color: highlightStance.color }}>
            Destacado por el profesor
          </p>
          <p className="mt-3 font-display text-2xl font-black leading-snug sm:text-3xl">{highlighted.text}</p>
        </div>
      )}
      <WallColumns
        stances={payload.stances}
        posts={runtime.posts}
        participants={participants}
        highlightedPostId={runtime.highlightedPostId}
        size="lg"
        maskNames={false}
      />
    </Shell>
  );
}
